import { Alert, Button, Space } from 'antd';
import { invoke } from '@tauri-apps/api/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDesktopGetAndroidRelease } from '@/api/generated/endpoints/desktop/desktop';
import { newer } from '@/lib/version';
import { androidApp, isDesktop, serverUrl } from '@/runtime';

const RECHECK_MS = 6 * 60 * 60 * 1000; // программу не закрывают сутками — проверяем и днём

interface UpdateInfo {
  version: string;
  current: string;
  notes: string | null;
}

/**
 * Программа для Windows (T-185): новая версия на сервере склада → плашка «Обновить».
 * Установщик закроет программу и запустит новую; очередь и вход сохраняются.
 */
export function UpdateBanner() {
  return androidApp() ? <AndroidUpdateBanner /> : <DesktopUpdateBanner />;
}

function DesktopUpdateBanner() {
  const { t } = useTranslation();
  const [update, setUpdate] = useState<UpdateInfo | null>(null);
  const [installing, setInstalling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const server = serverUrl();
    if (!isDesktop || !server) return;
    const check = () => {
      // Нет связи или выпуска — молча: обновление подождёт до следующей проверки.
      invoke<UpdateInfo | null>('check_update', { server }).then(setUpdate, () => undefined);
    };
    check();
    const timer = window.setInterval(check, RECHECK_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, []);

  if (!update) return null;
  return (
    <Alert
      type="info"
      showIcon
      style={{ marginBottom: 12 }}
      message={t('update.available', { version: update.version, current: update.current })}
      description={
        <Space direction="vertical" size={4}>
          {update.notes && <span>{update.notes}</span>}
          {error && <Alert type="error" showIcon message={t('update.failed', { error })} />}
        </Space>
      }
      action={
        <Button
          type="primary"
          loading={installing}
          onClick={() => {
            setInstalling(true);
            setError(null);
            invoke('install_update').catch((e: unknown) => {
              setInstalling(false);
              setError(String(e));
            });
          }}
        >
          {t('update.install')}
        </Button>
      }
    />
  );
}

/**
 * Приложение для Android (T-173): новая версия APK на сервере → «Скачать». Установку
 * подтверждает сам Android (разрешение «Установка из этого источника» — один раз).
 */
function AndroidUpdateBanner() {
  const { t } = useTranslation();
  const android = androidApp();
  const release = useDesktopGetAndroidRelease({
    query: { retry: false, refetchInterval: RECHECK_MS },
  });
  const data = release.data && typeof release.data === 'object' ? release.data : null;
  const current = android?.appVersion() ?? '';
  if (!android || !data || !newer(data.version, current)) return null;
  return (
    <Alert
      type="info"
      showIcon
      style={{ marginBottom: 12 }}
      message={t('update.availableApp', { version: data.version, current })}
      description={t('update.apkHint')}
      action={
        <Button
          type="primary"
          onClick={() => {
            android.openUrl(data.url);
          }}
        >
          {t('update.downloadApk')}
        </Button>
      }
    />
  );
}
